import { useCallback, useEffect, useMemo, useState } from 'react';

import useLocalStorage from '@hooks/useLocalStorage';
import isEqual from 'lodash/fp/isEqual';

import { getStorage as getStorageValue } from '@src/utils';

import { SETTINGS_STORAGE_KEY } from '../settings-config';
import type { SettingsProviderProps, SettingsState } from '../types';
import { SettingsContext } from './settings-context';

// ----------------------------------------------------------------------

export function SettingsProvider({
  children,
  defaultSettings,
  storageKey = SETTINGS_STORAGE_KEY,
}: SettingsProviderProps) {
  const [state, setState] = useLocalStorage<SettingsState>(storageKey, defaultSettings);

  const [openDrawer, setOpenDrawer] = useState(false);

  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const canReset = !isEqual(state, defaultSettings);

  const onReset = useCallback(() => {
    setState(defaultSettings);
  }, [defaultSettings, setState]);

  // Version check and reset handling
  useEffect(() => {
    const storedValue = getStorageValue<SettingsState>(storageKey);

    if (storedValue) {
      try {
        if (!storedValue.version || storedValue.version !== defaultSettings.version) {
          onReset();
        }
      } catch {
        onReset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customSetField = useCallback(
    (fieldName: keyof SettingsState, value: string | number | boolean | undefined) =>
      setState(() => ({ ...state, [fieldName]: value })),
    [setState, state],
  );

  const memoizedValue = useMemo(
    () => ({
      canReset,
      onReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      state,
      setState,
      setField: customSetField,
    }),
    [canReset, onReset, openDrawer, onCloseDrawer, onToggleDrawer, state, setState, customSetField],
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
